import { apiErrorField, customFetch } from '@agos/api-client'
import {
  getGetAuthMyPermissionsQueryKey,
  getGetSettingsPermissionsAuditQueryKey,
  getGetSettingsPermissionsEmployeesQueryKey,
  getGetSettingsPermissionsQueryKey,
} from '@agos/api-client/auth'
import { getGetEmployeesQueryKey } from '@agos/api-client/hr'
import type {
  Employee,
  EmployeePermissions,
  PermissionAuditEntry,
  RoleEdge,
  RoleAbilities,
  RoleView,
  RolePerson,
  RoleDefaults,
  RoleListReach,
  RolesViewer,
  RolesOverview,
  RoleStart,
  CreateRoleRequest,
  DeleteRoleResponse,
  RolePreviewRequest,
  RoleRefusedRight,
  RoleAffectedPerson,
  RolePreview,
  ReplaceRolePermissionsRequest,
  ReplaceRolePermissionsResponse,
  RoleDelegationEdge,
  RoleDelegationsRequest,
  RoleDelegationsResponse,
  RoleOption,
  RoleOptions,
  AssignRoleRequest,
} from '@agos/api-client/model'
import type { RoleColor } from '@agos/shared'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type InvalidateQueryFilters,
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query'

/**
 * The roles screen's requests - `FRONTEND.md` §9.752 and §9.753 on `BACKEND.md` §10A.408 (owner O119).
 *
 * **`customFetch` rather than a generated hook, for §9.509's reason.** The endpoints are written by a parallel
 * backend lane and are not in the committed spec yet, so no hook exists. Every path is on `typed-fetches.test.ts`'s
 * `AWAITING_GENERATED_CLIENT`, which **fails the day the client is regenerated** and says to move these calls to the
 * generated functions; `src/generated` is not touched.
 *
 * **Every type is named exactly as the server's record** (§10A.408's wire list). A record's simple name becomes the
 * OpenAPI schema name and then the generated model, so at integration each type here is swapped for the generated
 * one of the same name - and a field the two disagree on becomes a compile error rather than a screen reading
 * `undefined`, which is §3.122's defect. Nullable fields are `T | null`, as the contract writes them; a request's
 * optional fields are optional.
 *
 * **The query keys are the paths**, orval's own convention (`['/api/v1/employees']`), so the generated hooks that
 * replace these share their cache entries and no invalidation needs rewriting.
 */

// ─── the wire ────────────────────────────────────────────────────────────────









/** Where a new role's rights start from. */
// §9.513 - the shapes the server declares, now generated (O119 integration); re-exported so callers keep one import.
export type {
  RoleEdge,
  RoleAbilities,
  RoleView,
  RolePerson,
  RoleDefaults,
  RoleListReach,
  RolesViewer,
  RolesOverview,
  RoleStart,
  CreateRoleRequest,
  DeleteRoleResponse,
  RolePreviewRequest,
  RoleRefusedRight,
  RoleAffectedPerson,
  RolePreview,
  ReplaceRolePermissionsRequest,
  ReplaceRolePermissionsResponse,
  RoleDelegationEdge,
  RoleDelegationsRequest,
  RoleDelegationsResponse,
  RoleOption,
  RoleOptions,
  AssignRoleRequest,
}

export type RoleStartKind = 'defaults' | 'role' | 'person' | 'empty'



/** `PATCH /settings/roles/{key}` - a custom role's name, description, colour and place. */
export interface UpdateRoleRequest {
  name?: string
  description?: string | null
  color?: RoleColor | null
  sortOrder?: number
}














// ─── the requests ────────────────────────────────────────────────────────────

export const ROLES_PATH = '/api/v1/settings/roles'

export async function fetchRolesOverview(signal?: AbortSignal): Promise<RolesOverview> {
  return customFetch<RolesOverview>('/api/v1/settings/roles', { signal })
}

export async function createRole(data: CreateRoleRequest): Promise<RoleView> {
  return customFetch<RoleView>('/api/v1/settings/roles', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateRole(key: string, data: UpdateRoleRequest): Promise<RoleView> {
  return customFetch<RoleView>(`/api/v1/settings/roles/${String(key)}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

/**
 * Deletes a custom role. With members and no `moveTo` the server answers 409 `ROLE_HAS_MEMBERS` with the count;
 * with `moveTo` it moves them, deletes the role's rights and delegations, and the role, in one transaction.
 */
export async function deleteRole(
  key: string,
  params: { moveTo?: string | null } = {},
): Promise<DeleteRoleResponse> {
  const moveTo = params.moveTo?.trim() ?? ''
  const query = moveTo === '' ? '' : `?moveTo=${encodeURIComponent(moveTo)}`
  return customFetch<DeleteRoleResponse>(`/api/v1/settings/roles/${String(key)}${query}`, {
    method: 'DELETE',
  })
}

/** The server's plan for a proposed set, writing nothing - the rail's *Koho sa to týka*. */
export async function previewRolePermissions(
  key: string,
  data: RolePreviewRequest,
  signal?: AbortSignal,
): Promise<RolePreview> {
  return customFetch<RolePreview>(`/api/v1/settings/roles/${String(key)}/permissions/preview`, {
    method: 'POST',
    body: JSON.stringify(data),
    signal,
  })
}

export async function replaceRolePermissions(
  key: string,
  data: ReplaceRolePermissionsRequest,
): Promise<ReplaceRolePermissionsResponse> {
  return customFetch<ReplaceRolePermissionsResponse>(
    `/api/v1/settings/roles/${String(key)}/permissions`,
    { method: 'PUT', body: JSON.stringify(data) },
  )
}

export async function replaceDelegations(
  data: RoleDelegationsRequest,
): Promise<RoleDelegationsResponse> {
  return customFetch<RoleDelegationsResponse>('/api/v1/settings/roles/delegations', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function fetchRoleOptions(
  employeeId: number,
  signal?: AbortSignal,
): Promise<RoleOptions> {
  return customFetch<RoleOptions>(`/api/v1/settings/roles/options/${String(employeeId)}`, {
    signal,
  })
}

/** Moves a person into a role: `system_role` becomes its base and `role_key` the custom key or null. */
export async function assignRole(id: number, data: AssignRoleRequest): Promise<Employee> {
  return customFetch<Employee>(`/api/v1/employees/${String(id)}/role`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

// ─── the cache ───────────────────────────────────────────────────────────────

export function getRolesOverviewQueryKey() {
  return [ROLES_PATH] as const
}

export function getRoleOptionsQueryKey(employeeId: number) {
  return [`${ROLES_PATH}/options/${String(employeeId)}`] as const
}

/**
 * The preview's key carries the proposed set **sorted and without repeats**, so ticking two rights in the other
 * order asks nothing new - and the body sends the same list, so the key always describes what was asked.
 */
export function getRolePreviewQueryKey(key: string, data: RolePreviewRequest) {
  return [
    `${ROLES_PATH}/${String(key)}/permissions/preview`,
    { permissions: normalisedRights(data.permissions) },
  ] as const
}

function normalisedRights(rights: readonly string[]): string[] {
  return [...new Set(rights)].sort()
}

/** Whether a cached query is one person's role options - `['/api/v1/settings/roles/options/{employeeId}']`. */
export function isRoleOptionsQueryKey(queryKey: QueryKey): boolean {
  const first = queryKey[0]
  return typeof first === 'string' && /^\/api\/v1\/settings\/roles\/options\/\d+$/.test(first)
}

/** Whether a cached query is one role's rights preview - `['/api/v1/settings/roles/{key}/permissions/preview', {...}]`. */
export function isRolePreviewQueryKey(queryKey: QueryKey): boolean {
  const first = queryKey[0]
  return (
    typeof first === 'string' && /^\/api\/v1\/settings\/roles\/[^/]+\/permissions\/preview$/.test(first)
  )
}

/**
 * The reads a role change can move, each by the key its reader caches it under.
 *
 * `people` is `GET /employees` (a person's `systemRole` and `roleKey`), `permissions` is
 * `GET /settings/permissions/employees` (a person's rights and the server's `allowed`), `matrix` is
 * `GET /settings/permissions` (the built-in roles' rows), `mine` is `GET /auth/my-permissions` (the reader's own
 * menu) and `trail` is `GET /settings/permissions/audit` - every one of these writes leaves a row there.
 */
export type RoleRead =
  | 'overview'
  | 'options'
  | 'previews'
  | 'people'
  | 'permissions'
  | 'matrix'
  | 'mine'
  | 'trail'

const READ_FILTERS: Record<RoleRead, InvalidateQueryFilters> = {
  overview: { queryKey: getRolesOverviewQueryKey() },
  options: { predicate: (query) => isRoleOptionsQueryKey(query.queryKey) },
  previews: { predicate: (query) => isRolePreviewQueryKey(query.queryKey) },
  people: { queryKey: getGetEmployeesQueryKey() },
  permissions: { queryKey: getGetSettingsPermissionsEmployeesQueryKey() },
  matrix: { queryKey: getGetSettingsPermissionsQueryKey() },
  mine: { queryKey: getGetAuthMyPermissionsQueryKey() },
  // Without its params, so every page and filter of the trail is covered.
  trail: { queryKey: getGetSettingsPermissionsAuditQueryKey() },
}

/**
 * What each write can move, and nothing else.
 *
 * - A new role is a new card and a new option; a rename moves the card only - no person's row carries a name.
 * - A delete moves its members into another role, so their rows, rights and options move with them.
 * - A rights save moves the role's rows (the built-ins' too, which `GET /settings/permissions` answers), its
 *   members' rights and any preview drawn from the old set. Never the reader's own menu: a delegate may not edit
 *   their own role (`ROLE_OWN`) and a main administrator's is everything already.
 * - A delegation save moves who may assign and edit what, which is the overview's `you`, the options and the
 *   previews' refusals.
 * - An assignment moves one person - and a main administrator may move themselves, so the reader's own menu too.
 *
 * `GET /auth/me`'s `managesRoles` moves with a delegation save as well; the shell reads `/auth/me` outside react-query
 * (`auth-context.tsx`), so there is no cache entry here to drop and the next sign-in or reload picks it up.
 */
export const ROLE_WRITE_REACH = {
  create: ['overview', 'options', 'trail'],
  update: ['overview', 'trail'],
  delete: ['overview', 'options', 'previews', 'people', 'permissions', 'trail'],
  replacePermissions: ['overview', 'previews', 'permissions', 'matrix', 'trail'],
  replaceDelegations: ['overview', 'options', 'previews', 'trail'],
  assign: ['overview', 'options', 'previews', 'people', 'permissions', 'mine', 'trail'],
} as const satisfies Record<string, readonly RoleRead[]>

export type RoleWrite = keyof typeof ROLE_WRITE_REACH

/**
 * Drops the reads `write` can move. Awaited by the mutations, so a dialog closes on fresh data rather than on the
 * figures it just changed.
 */
export async function refreshAfterRoleWrite(queryClient: QueryClient, write: RoleWrite): Promise<void> {
  const reads: readonly RoleRead[] = ROLE_WRITE_REACH[write]
  await Promise.all(reads.map((read) => queryClient.invalidateQueries(READ_FILTERS[read])))
}

// ─── the hooks ───────────────────────────────────────────────────────────────

/** A query's own options, as orval's hooks take them under `query`; the key and the fetch are the hook's. */
export type RoleQueryOptions<T> = Omit<UseQueryOptions<T, Error, T, QueryKey>, 'queryKey' | 'queryFn'>

export function useRolesOverview(options?: { query?: RoleQueryOptions<RolesOverview> }) {
  return useQuery({
    ...options?.query,
    queryKey: getRolesOverviewQueryKey(),
    queryFn: ({ signal }) => fetchRolesOverview(signal),
  })
}

/** One person's role picker; asks nothing until there is a person. */
export function useRoleOptions(
  employeeId: number,
  options?: { query?: RoleQueryOptions<RoleOptions> },
) {
  return useQuery({
    enabled: employeeId > 0,
    ...options?.query,
    queryKey: getRoleOptionsQueryKey(employeeId),
    queryFn: ({ signal }) => fetchRoleOptions(employeeId, signal),
  })
}

/**
 * The rail's preview of a proposed set - a **query**, though the method is `POST`, because it writes nothing and
 * the rail follows the ticks: keyed by the set, a quick second tick does not race the first, an answer for a set
 * the editor has moved past is not drawn, and going back to a set already asked about costs no request.
 */
export function usePreviewRolePermissions(
  key: string,
  data: RolePreviewRequest,
  options?: { query?: RoleQueryOptions<RolePreview> },
) {
  const queryKey = getRolePreviewQueryKey(key, data)
  return useQuery({
    enabled: key !== '',
    ...options?.query,
    queryKey,
    queryFn: ({ signal }) => previewRolePermissions(key, queryKey[1], signal),
  })
}

export function createRoleMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: ({ data }: { data: CreateRoleRequest }) => createRole(data),
    onSuccess: () => refreshAfterRoleWrite(queryClient, 'create'),
  }
}

export function updateRoleMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: ({ key, data }: { key: string; data: UpdateRoleRequest }) => updateRole(key, data),
    onSuccess: () => refreshAfterRoleWrite(queryClient, 'update'),
  }
}

export function deleteRoleMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: ({ key, params }: { key: string; params?: { moveTo?: string | null } }) =>
      deleteRole(key, params),
    onSuccess: () => refreshAfterRoleWrite(queryClient, 'delete'),
  }
}

export function replaceRolePermissionsMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: ({ key, data }: { key: string; data: ReplaceRolePermissionsRequest }) =>
      replaceRolePermissions(key, data),
    onSuccess: () => refreshAfterRoleWrite(queryClient, 'replacePermissions'),
  }
}

export function replaceDelegationsMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: ({ data }: { data: RoleDelegationsRequest }) => replaceDelegations(data),
    onSuccess: () => refreshAfterRoleWrite(queryClient, 'replaceDelegations'),
  }
}

export function assignRoleMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: ({ id, data }: { id: number; data: AssignRoleRequest }) => assignRole(id, data),
    onSuccess: () => refreshAfterRoleWrite(queryClient, 'assign'),
  }
}

export function useCreateRole() {
  const queryClient = useQueryClient()
  return useMutation(createRoleMutationOptions(queryClient))
}

export function useUpdateRole() {
  const queryClient = useQueryClient()
  return useMutation(updateRoleMutationOptions(queryClient))
}

export function useDeleteRole() {
  const queryClient = useQueryClient()
  return useMutation(deleteRoleMutationOptions(queryClient))
}

export function useReplaceRolePermissions() {
  const queryClient = useQueryClient()
  return useMutation(replaceRolePermissionsMutationOptions(queryClient))
}

export function useReplaceDelegations() {
  const queryClient = useQueryClient()
  return useMutation(replaceDelegationsMutationOptions(queryClient))
}

export function useAssignRole() {
  const queryClient = useQueryClient()
  return useMutation(assignRoleMutationOptions(queryClient))
}

// ─── refusals ────────────────────────────────────────────────────────────────

/**
 * The codes a roles refusal carries - §10A.408, and O113's (§10A.394), whose sentences the roles endpoints reuse word for
 * word **and whose codes they send too**: `ONLY_A_MAIN_ADMIN_MAKES_ONE` (only a main administrator gives the role),
 * `MAIN_ADMIN_RECORD` (another main administrator's record is not edited) and `LAST_MAIN_ADMIN` (the last active one
 * keeps it) - on a refused assignment and on a refused option in the role picker alike, so a screen keys on them as it
 * keys on the rest. (The contract counts *O113's two*; the server names three, the record rule being O113's too.)
 */
export const ROLE_REFUSAL_CODES = [
  'ROLE_MAIN_ADMIN_ONLY',
  'ROLE_NOT_MANAGED',
  'ROLE_OWN',
  'ROLE_TOP',
  'ROLE_BEYOND_YOURS',
  'ROLE_LOCKED',
  'ROLE_CYCLE',
  'ROLE_HAS_MEMBERS',
  'ROLE_STALE',
  'ROLE_NAME_TAKEN',
  'ROLE_NAME_BUILTIN',
  'ROLE_LIMIT',
  'ROLE_INACTIVE_PERSON',
  'ROLE_UNKNOWN',
  'ONLY_A_MAIN_ADMIN_MAKES_ONE',
  'MAIN_ADMIN_RECORD',
  'LAST_MAIN_ADMIN',
] as const

export type RoleRefusalCode = (typeof ROLE_REFUSAL_CODES)[number]

export function isRoleRefusalCode(value: string | null | undefined): value is RoleRefusalCode {
  return ROLE_REFUSAL_CODES.some((code) => code === value)
}

/**
 * The code of a refused roles request, or null for anything else - a failure that never reached the server, or a code
 * this client does not know. A screen keys on the code (a 409 `ROLE_STALE` offers a reload) and shows the server's own
 * sentence for the rest.
 */
export function roleRefusalCode(error: unknown): RoleRefusalCode | null {
  const code = apiErrorField(error, 'code')
  return isRoleRefusalCode(code) ? code : null
}

// ─── fields §10A.408 adds to models the client already has ───────────────────

/*
 * Each is read through `in` rather than a cast: today's generated model does not declare the field and the
 * regenerated one will, and `in` narrows correctly against both, where a cast would have to come out at integration.
 * A server from before the deploy answers none of them, and absent reads as null - never as a role or a right.
 */

/** `GET /employees` and `GET /settings/permissions/employees`: the custom role, or null for a built-in. */
export function roleKeyOf(row: Employee | EmployeePermissions): string | null {
  return 'roleKey' in row ? textOrNull(row.roleKey) : null
}

/**
 * `GET /settings/permissions/employees`: what the server lets the person do, key by key (`allowedSet`, §10A.407) -
 * the *held* of *Čo smie a prečo*. Null when the server did not say, which is not the same as nothing.
 */
export function allowedOf(row: EmployeePermissions): string[] | null {
  if (!('allowed' in row) || !Array.isArray(row.allowed)) return null
  return row.allowed.filter((key): key is string => typeof key === 'string')
}

/** `GET /settings/permissions/audit`: the actor's role when they acted, `platform` for the Control Tower, or null. */
export function actorRoleOf(entry: PermissionAuditEntry): string | null {
  return 'actorRole' in entry ? textOrNull(entry.actorRole) : null
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}
